import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { toast } from 'react-toastify';
import { disputesApi } from '../../api/disputes';
import {
  PageHeader,
  PageFilters,
  DataTable,
  TablePaginationBar,
  StatusBadge,
  ActionMenu,
} from '../../components/ui';
import type { Column } from '../../components/ui';
import { PAGE_SIZE } from '../../constants';
import { formatDate, paginate, matchesSearch } from '../../utils';
import type { Complaint, ComplaintStatus } from '../../types';

const STATUS_OPTIONS = [
  { label: 'All Statuses', value: 'all' },
  { label: 'Open', value: 'Open' },
  { label: 'Under Review', value: 'Under Review' },
  { label: 'Resolved', value: 'Resolved' },
  { label: 'Rejected', value: 'Rejected' },
  { label: 'Closed', value: 'Closed' },
];

const ComplaintsPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<Complaint[]>([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);

  useEffect(() => {
    disputesApi
      .getOngoingDisputes({ page: 1, limit: 1000 })
      .then((res) => setRows(res.disputes))
      .catch(() => toast.error('Failed to load complaints'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    return rows.filter((row) => {
      const matches =
        !search.trim() ||
        matchesSearch(row.id, search) ||
        matchesSearch(row.jobId, search);
      const statusOk = status === 'all' || row.status === (status as ComplaintStatus);
      return matches && statusOk;
    });
  }, [rows, search, status]);

  useEffect(() => {
    setPage(1);
  }, [search, status]);

  const paged = paginate(filtered, page, PAGE_SIZE);

  const columns: Column<Complaint>[] = [
    {
      id: 'id',
      label: 'ID',
      minWidth: 100,
      render: (row) => (
        <Typography variant="body2" fontWeight={600}>
          {row.id}
        </Typography>
      ),
    },
    {
      id: 'jobId',
      label: 'Job ID',
      minWidth: 100,
      render: (row) => row.jobId,
    },
    {
      id: 'submittedBy',
      label: 'Submitted By',
      minWidth: 140,
      render: (row) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {row.submittedBy}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row.submittedByType}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'against',
      label: 'Against',
      minWidth: 140,
      render: (row) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {row.against}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row.againstType}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'type',
      label: 'Type',
      minWidth: 120,
      render: (row) => row.type,
    },
    {
      id: 'createdAt',
      label: 'Created',
      minWidth: 110,
      render: (row) => formatDate(row.createdAt),
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 120,
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      id: 'actions',
      label: 'Actions',
      align: 'right',
      render: (row) => (
        <ActionMenu
          actions={[
            {
              label: 'View',
              icon: <VisibilityOutlinedIcon fontSize="small" />,
              onClick: () => navigate(`/complaints/${row.id}`),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Complaints"
        subtitle="Review and resolve marketplace disputes"
      />
      <PageFilters
        search={search}
        searchPlaceholder="Search complaint or job ID…"
        onSearchChange={setSearch}
        filters={[
          {
            id: 'status',
            label: 'Status',
            value: status,
            options: STATUS_OPTIONS,
            onChange: setStatus,
          },
        ]}
      />
      <DataTable
        columns={columns}
        rows={paged}
        loading={loading}
        getRowId={(row) => row.id}
        emptyTitle="No complaints found"
      />
      <TablePaginationBar
        page={page}
        pageSize={PAGE_SIZE}
        total={filtered.length}
        onChange={setPage}
      />
    </Box>
  );
};

export default ComplaintsPage;
